import { useState, useMemo, useCallback, useEffect } from 'react';
import Fuse from 'fuse.js/dist/fuse.basic.esm';

import dataDictionary from '../../assets/data-dictionary/dataDictionary.json';
import DraggableStaticList from '../builder/DraggableStaticList';
import DraggableAttribute from '../builder/DraggableAttribute';
import { TYPES } from '../../reducers/packageBuilderReducer';
import {
  isHiddenAttribute,
  isNewCategory,
  traverse,
} from '../../helpers/dataDictionary';
import SearchBox from '../inputs/SearchBox';
import { Tooltip } from 'react-tooltip';

let searchList = [];

for (const [_, value, __, ___] of traverse(dataDictionary)) {
  if (value.attributes) {
    value.attributes.forEach(({ attributeDescription, id, attrs }) => {
      if (isHiddenAttribute(attrs?.key)) return;
      const categories = id.split('___');

      searchList.push({
        attributeDescription: attributeDescription.toLowerCase(),
        id,
        category: categories[0],
        subCategory0: categories[1] || undefined,
        subCategory1: categories[2] || undefined,
        subCategory2: categories[3] || undefined,
        key: attrs.key,
      });
    });
  }
}

const fuse = new Fuse(searchList, {
  minMatchCharLength: 3,
  threshold: 0.2,
  includeScore: true,
  keys: [
    'id',
    'attributeDescription',
    'category',
    'subCategory0',
    'subCategory1',
    'subCategory2',
    'key',
  ],
  ignoreLocation: true,
  ignoreFieldNorm: true,
});

function hasNestedChildrenInResults(children, searchResults) {
  let hasNestedChildAttributes = false;

  for (const [key, value] of traverse(children)) {
    if (
      key === 'attributes' &&
      value.length &&
      value.some((attribute) =>
        searchResults.find(({ item }) => attribute.id === item.id)
      )
    ) {
      hasNestedChildAttributes = true;
      break;
    }
  }

  return hasNestedChildAttributes;
}

export default function EnhancementsDataDictionary() {
  const [searchStr, setSearchStr] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const expandAllCategories = useMemo(() => searchStr !== '', [searchStr]);

  useEffect(() => {
    setSearchResults(fuse.search(searchStr).slice(0, 10));
  }, [searchStr]);

  const renderChildren = useCallback(
    (children) => {
      let nodes = [];

      Object.entries(children).forEach(([key, children]) => {
        if (key === 'attributes') {
          if (children.length) {
            const listItems = [];

            const filteredChildren =
              searchStr !== ''
                ? children.filter((attribute) =>
                    searchResults.find(({ item }) => attribute.id === item.id)
                  )
                : children;
            filteredChildren.forEach((attribute) => {
              // Skip variables withheld while they're reworked
              if (isHiddenAttribute(attribute.attrs.key)) {
                return;
              }
              if (
                !listItems.find(
                  (listItem) => listItem.key === attribute.attrs.key
                )
              ) {
                listItems.push({
                  component: (
                    <div key={attribute.id} className="flex gap-2">
                      <DraggableAttribute
                        name={attribute.attrs.key}
                        variableKey={attribute.attrs.key}
                        id={attribute.id}
                        type={TYPES.VARIABLE}
                        tooltipId="data-dictionary-tooltip"
                        tooltipContent={attribute.attributeDescription}
                      />
                    </div>
                  ),
                  key: attribute.attrs.key,
                });
              }
            });

            nodes.push(
              <div>{listItems.map(({ component }) => component)}</div>
            );
          }
          return;
        }

        // categoryDescription is metadata on the category node, not a
        // subcategory — skip it so it isn't rendered as a nested folder.
        if (key === 'categoryDescription') {
          return;
        }

        const description = (children as any)?.categoryDescription;

        if (searchStr === '') {
          nodes.push(
            <DraggableStaticList
              label={key}
              description={description}
              tooltipId="data-dictionary-tooltip"
              items={renderChildren(children)}
              initialOpen={expandAllCategories}
            />
          );
          return;
        }

        if (hasNestedChildrenInResults(children, searchResults)) {
          nodes.push(
            <DraggableStaticList
              label={key}
              description={description}
              tooltipId="data-dictionary-tooltip"
              items={renderChildren(children)}
              initialOpen={expandAllCategories}
            />
          );
        }
      });

      return nodes;
    },
    [expandAllCategories, searchResults, searchStr]
  );

  const filteredDataDictionary = useMemo(
    () =>
      Object.entries(dataDictionary)
        .filter(([key]) => key)
        .map(([key, children]) => {
          return searchStr === '' ||
            hasNestedChildrenInResults(children, searchResults) ? (
            <div key={key}>
              <DraggableStaticList
                label={key}
                isNew={isNewCategory(key)}
                description={(children as any)?.categoryDescription}
                tooltipId="data-dictionary-tooltip"
                items={renderChildren(children)}
                initialOpen={expandAllCategories}
              />
            </div>
          ) : null;
        }),
    [expandAllCategories, renderChildren, searchResults, searchStr]
  );

  return (
    <div className="flex flex-col w-full">
      <SearchBox setParentSearchStr={setSearchStr} />
      <ul className="w-full">{filteredDataDictionary}</ul>
      <Tooltip
        id="data-dictionary-tooltip"
        place="right"
        style={{ maxWidth: 300 }}
        delayShow={1000}
      />
    </div>
  );
}
